import { useQuery } from '@tanstack/react-query'
import { useWalletTokens } from './useWalletTokens'
import { fetchLastPriceForMints, fetchMetadataForMints } from 'utils/tokens'
import { USDC_MINT } from 'utils/constants'
import { TokenMetadataItem } from 'types/token'
import { milliseconds } from 'date-fns'

const metadataCache = new Map<string, TokenMetadataItem[]>()

interface WalletTokenWithPrice extends TokenMetadataItem {
  price: number
  uiAmount: number
  value: number
}

const fetchWalletTokensData = async (
  mints: string[],
): Promise<WalletTokenWithPrice[]> => {
  try {
    const [meta, prices] = await Promise.all([
      fetchMetadataForMints(mints, metadataCache),
      fetchLastPriceForMints(mints),
    ])

    if (meta && meta?.length) {
      const walletTokensData: WalletTokenWithPrice[] = []
      for (const token of meta) {
        let lastPrice = prices.find((t) => t.mint === token.mint)?.price || 0
        if (!lastPrice && token?.mint === USDC_MINT) {
          lastPrice = 1
        }
        const item: WalletTokenWithPrice = {
          ...token,
          price: lastPrice,
          uiAmount: 0,
          value: 0,
        }
        walletTokensData.push(item)
      }
      return walletTokensData
    } else return []
  } catch (e) {
    console.error('failed to fetch wallet tokens data for mints', e)
    return []
  }
}

export const useWalletTokensWithPrice = () => {
  const { walletTokens, loading: loadingWalletTokens } = useWalletTokens()

  const walletTokensMints =
    walletTokens?.map((token) => token.mint.toString()) || []

  const { data: walletTokensMeta, isInitialLoading: loadingWalletTokensMeta } =
    useQuery({
      queryKey: ['wallet-token-meta', walletTokensMints],
      queryFn: () => fetchWalletTokensData(walletTokensMints),
      cacheTime: milliseconds({ minutes: 10 }),
      staleTime: milliseconds({ minutes: 10 }),
      retry: 3,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      enabled: !!walletTokens?.length,
    })

  const sortedTokens = walletTokensMeta
    ?.map((token) => {
      const walletToken = walletTokens?.find(
        (wt) => wt.mint.toString() === token.mint,
      )
      const uiAmount = walletToken?.uiAmount || 0
      const value = token.price * uiAmount

      return {
        ...token,
        uiAmount,
        value,
      }
    })
    .sort((a, b) => b.value - a.value)

  const loading = loadingWalletTokens || loadingWalletTokensMeta

  return {
    tokens: sortedTokens || [],
    loading,
  }
}
